import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { updateMenu } from '../../../shared/api/menus.js'
import type { Menu } from '../../../shared/types/models.js'
import { validateNameField } from '../../../shared/utils/catalog.js'
import { toFieldErrors, type FieldErrors } from '../../../shared/utils/formErrors.js'
import StatusBadge from './StatusBadge.js'

type EditStatus = 'idle' | 'saving' | 'success' | 'error'

interface Props {
  organizationId: string
  menu: Menu
  disabled: boolean
}

export default function MenuEditForm({ organizationId, menu, disabled }: Props) {
  const { t } = useTranslation()

  const [name, setName] = useState(menu.name)
  const [description, setDescription] = useState(menu.description ?? '')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [status, setStatus] = useState<EditStatus>('idle')

  useEffect(() => {
    setName(menu.name)
    setDescription(menu.description ?? '')
    setErrors({})
    setStatus('idle')
  }, [menu])

  function handleChange() {
    if (status === 'success' || status === 'error') setStatus('idle')
    if (errors.name || errors.general) setErrors({})
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (disabled) return

    const fieldErrors: FieldErrors = {}
    const nameError = validateNameField(name)
    if (nameError === 'required') fieldErrors.name = t('error.fieldRequired')
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    setStatus('saving')
    try {
      const updated = await updateMenu(organizationId, menu.id, {
        name: name.trim(),
        description: description.trim() || undefined,
      })
      setName(updated.name)
      setDescription(updated.description ?? '')
      setErrors({})
      setStatus('success')
    } catch (err: unknown) {
      setErrors(toFieldErrors(err, ['name'], t('error.serverError')))
      setStatus('error')
    }
  }

  const saving = status === 'saving'

  return (
    <div className="settings-card">
      {status === 'success' && (
        <div className="form-success-banner" role="status">
          {t('menu.saveSuccess')}
        </div>
      )}
      {errors.general && <div className="form-error-banner">{errors.general}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-field">
          <label htmlFor="menuEditName">{t('menu.name')} *</label>
          <input
            id="menuEditName"
            type="text"
            value={name}
            onChange={e => {
              setName(e.target.value)
              handleChange()
            }}
            maxLength={200}
            required
            disabled={saving || disabled}
            autoComplete="off"
          />
          {errors.name && <span className="field-error">{errors.name}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="menuEditSlug">{t('menu.slug')}</label>
          <input id="menuEditSlug" type="text" value={menu.slug ?? ''} readOnly disabled />
          <span className="field-hint">{t('menu.slugReadonlyHint')}</span>
        </div>

        <div className="form-field">
          <label htmlFor="menuEditDescription">{t('menu.description')}</label>
          <textarea
            id="menuEditDescription"
            value={description}
            onChange={e => {
              setDescription(e.target.value)
              handleChange()
            }}
            rows={4}
            maxLength={2000}
            disabled={saving || disabled}
          />
        </div>

        {!disabled && (
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t('menu.saving') : t('menu.save')}
          </button>
        )}
      </form>

      {disabled && (
        <p className="menu-readonly-note">
          <StatusBadge status={menu.status} />
          {menu.status === 'ARCHIVED' ? t('menu.archivedNote') : t('menu.readonlyHint')}
        </p>
      )}
    </div>
  )
}